import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import apiClient from '../services/apiClient'

// ─── Workshop info ────────────────────────────────────────────────
const WORKSHOP = {
  id: 'ai4sdlc-2026-09-27',
  name: 'AI4SDLC Workshop',
  date: '27/09/2026',
  time: '20:00 – 22:00',
  platform: 'Google Meet',
}

// ─── Step ────────────────────────────────────────────────────────
function Step({ num, title, sub, desc }: { num: number; title: string; sub: string; desc: string }) {
  return (
    <div className="flex items-start gap-3 relative z-10">
      <div className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs shrink-0 shadow-sm bg-m2-primary text-m2-on-primary">
        {num}
      </div>
      <div className="pt-0.5 flex-1">
        <div className="flex flex-wrap items-baseline gap-1.5">
          <span className="font-semibold text-xs text-m2-primary">{title}</span>
          <span className="text-xs text-m2-secondary">→</span>
          <span className="text-xs text-m2-on-surface">{sub}</span>
        </div>
        <p className="text-xs text-m2-on-surface-variant mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  )
}

// ─── Callout ──────────────────────────────────────────────────────
function Callout({ icon, text }: { icon: string; text: React.ReactNode }) {
  return (
    <div className="mt-3 p-3 rounded-xl flex items-start gap-2.5 text-xs bg-m2-surface-low border border-m2-outline-variant text-m2-primary">
      <span className="text-base shrink-0">{icon}</span>
      <p className="font-medium leading-relaxed">{text}</p>
    </div>
  )
}

// ─── YouTubeButton ────────────────────────────────────────────────
function YouTubeButton({ className, youtubeUrl }: { className?: string; youtubeUrl?: string | null }) {
  const handleClick = () => {
    if (youtubeUrl) {
      window.open(youtubeUrl, '_blank', 'noopener,noreferrer')
    } else {
      toast.info('🎬 Vui lòng chờ admin upload video lên YouTube!')
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={
        className ||
        'inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-colors border-2 border-red-500 text-red-600 hover:bg-red-50'
      }
    >
      <svg
        className="w-4 h-4 fill-current"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
      Xem video trên YouTube
    </button>
  )
}

// ─── Main ─────────────────────────────────────────────────────────
const WorkshopPage = () => {
  const [youtubeUrl, setYoutubeUrl] = useState<string | null>(null)

  // Fetch config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await apiClient.get<{ success: boolean; data: { youtube_url: string | null } }>(
          `/public/workshops/${WORKSHOP.id}`
        )
        if (res.data?.success && res.data?.data) {
          setYoutubeUrl(res.data.data.youtube_url)
        }
      } catch (err) {
        console.error('Failed to fetch workshop config', err)
      }
    }
    void fetchConfig()
  }, [])

  // Prevent lingering overflow lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  return (
    <div className="min-h-screen bg-m2-background" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* ═══════════════════════════════════════════════════════
          HERO  –  Centered layout
      ═══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden border-b border-m2-outline-variant bg-gradient-to-b from-m2-surface-low via-m2-surface to-m2-background">
        {/* dot pattern */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgb(var(--m2-primary)/0.12) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-20 text-center flex flex-col items-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 bg-m2-surface border border-m2-primary-container shadow-sm">
            <span className="w-2 h-2 rounded-full bg-m2-secondary" />
            <span className="text-xs font-bold tracking-widest uppercase text-m2-on-surface-variant">
              WORKSHOP ĐÃ KẾT THÚC
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4 text-m2-primary">
            AI4SDLC Workshop
          </h1>

          <p className="text-sm md:text-lg text-m2-on-surface-variant max-w-2xl mb-8 leading-relaxed">
            Khám phá quy trình phát triển phần mềm thế hệ mới với trí tuệ nhân tạo — tối ưu hoá
            toàn diện chu trình từ kiến trúc, đặc tả PRD đến hiện thực hoá sản phẩm.
          </p>

          {/* Meta pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-m2-surface border border-m2-outline-variant text-m2-on-surface shadow-sm">
              📅 {WORKSHOP.date} · {WORKSHOP.time}
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-m2-primary-container/40 border border-m2-primary/20 text-m2-primary">
              💰 Miễn phí
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm bg-m2-surface border border-m2-outline-variant text-m2-on-surface-variant">
              📹 Online qua Google Meet
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <YouTubeButton
              youtubeUrl={youtubeUrl}
              className="inline-flex items-center gap-2 px-8 h-12 rounded-xl font-semibold text-base shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg active:scale-[0.98] border-2 border-red-500 text-red-600 hover:bg-red-50"
            />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          CONTENT  –  2 project paths side by side
      ═══════════════════════════════════════════════════════ */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {/* Section header */}
        <div className="mb-8 text-center flex flex-col items-center">
          <div className="flex items-center justify-center gap-2.5 mb-2">
            <span className="w-3 h-6 rounded-full bg-m2-primary" />
            <span className="text-xs font-bold uppercase tracking-widest text-m2-secondary">
              Lộ trình chương trình
            </span>
            <span className="w-3 h-6 rounded-full bg-m2-primary" />
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-m2-primary">Nội dung Workshop</h2>
          <p className="text-base text-m2-on-surface-variant mt-2 leading-relaxed max-w-2xl">
            Chuẩn hóa cách tiếp cận kỹ thuật phần mềm tích hợp AI Agents — áp dụng cho cả dự án mới
            lẫn hệ thống đang vận hành.
          </p>
        </div>

        {/* ── 2 cols: Dự án mới | Dự án đã chạy ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* ── PATH 1: Dự án mới ── */}
          <div className="flex flex-col rounded-2xl bg-m2-surface border border-m2-outline-variant shadow-sm overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-m2-outline-variant bg-m2-surface-low">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg bg-m2-surface">
                  🆕
                </div>
                <div>
                  <h3 className="font-semibold text-base text-m2-primary">Dự án mới</h3>
                  <p className="text-xs text-m2-on-surface-variant">Từ con số không đến hệ thống sản xuất</p>
                </div>
              </div>
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-m2-surface text-m2-primary border border-m2-outline-variant shrink-0">
                Greenfield
              </span>
            </div>

            {/* Steps */}
            <div className="flex-1 px-6 py-6 flex flex-col gap-5 relative">
              <div className="absolute left-[1.4rem] top-6 bottom-6 w-0.5 bg-m2-outline-variant" />
              <Step num={1} title="PRD Workflow" sub="Phân tích & lập tài liệu yêu cầu" desc="Sử dụng bộ skill Elicitation, Gap Analysis, Create PRD, Data Model, Define Epics & User Story để chuẩn hóa toàn bộ yêu cầu." />
              <Step num={2} title="Architecture" sub="Dựng kiến trúc hệ thống" desc="Tạo C4 Model level 1, 2 và định nghĩa quy chuẩn coding cho từng thành phần, quy định bảo mật và coding convention." />
              <Step num={3} title="Spec Engine" sub="Đặc tả chi tiết từng User Story" desc="Từ danh sách US, AI tự động viết spec — BA dừng lại review thiết kế màn hình, sau đó AI đặc tả toàn bộ US và các API (gồm đặc tả + flow xử lý API)." />
              <Step num={4} title="Coding" sub="AI thực hiện coding theo spec" desc="Từ spec đã tạo ra, AI coding đúng yêu cầu — không cần giải thích lại context, không drift khỏi thiết kế." />
              <Step num={5} title="Testing" sub="Test tự động & fix bug vòng lặp" desc="Viết testcase, testscript và thực hiện test, ra báo cáo lỗi — tự động fix bug khi kết quả chưa đạt theo yêu cầu." />
            </div>

            <div className="px-6 pb-6">
              <Callout
                icon="💡"
                text={
                  <>
                    <strong>Trong quá trình Coding/Fix:</strong> Cập nhật tài liệu Software
                    Architecture (SA) hiện trạng để AI luôn có context chính xác, tránh code drift.
                  </>
                }
              />
            </div>
          </div>

          {/* ── PATH 2: Dự án đã chạy ── */}
          <div className="flex flex-col rounded-2xl bg-m2-surface border border-m2-outline-variant shadow-sm overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between gap-3 px-6 py-5 border-b border-m2-outline-variant bg-m2-surface-high/40">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg bg-m2-surface">
                  🔄
                </div>
                <div>
                  <h3 className="font-semibold text-base text-m2-primary">Dự án đã chạy</h3>
                  <p className="text-xs text-m2-on-surface-variant">Bảo trì, tái cấu trúc & bổ sung tính năng</p>
                </div>
              </div>
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-m2-surface-high text-m2-primary border border-m2-outline-variant shrink-0">
                Brownfield
              </span>
            </div>

            {/* Steps */}
            <div className="px-6 py-6 flex flex-col gap-5">
              <Step num={1} title="Architecture" sub="Reverse Engineering hiện trạng" desc="Sử dụng Repo Indexing và AST parser để AI map toàn bộ dependency graph của codebase sẵn có." />
              <Step num={2} title="CR (Change Request)" sub="Phân loại yêu cầu thay đổi" desc="AI phân định rủi ro kỹ thuật để rẽ nhánh xử lý tự động, tối ưu chi phí và giảm thiểu rủi ro." />
            </div>

            {/* Branch diagram */}
            <div className="mx-6 mb-6 p-5 rounded-xl bg-m2-surface-low border border-m2-outline-variant">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-4 text-m2-secondary">
                🔀 Phân nhánh xử lý AI
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl flex flex-col gap-2.5 bg-m2-surface border border-m2-outline-variant">
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-semibold text-sm text-m2-primary">CR Nhỏ</span>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-m2-surface-low text-m2-primary">
                      Nhanh
                    </span>
                  </div>
                  <p className="text-xs text-m2-on-surface-variant leading-relaxed">
                    Bug fix, cập nhật validation, sửa layout nhỏ.
                  </p>
                  <div className="pt-2 mt-auto flex items-center gap-1.5 text-xs font-semibold border-t border-m2-outline-variant text-m2-primary">
                    <span>Fix Coding</span>
                    <span className="text-m2-secondary">→</span>
                    <span>Testing</span>
                  </div>
                </div>
                <div className="p-4 rounded-xl flex flex-col gap-2.5 bg-m2-surface border border-m2-outline-variant">
                  <div className="flex items-center justify-between gap-1">
                    <span className="font-semibold text-sm text-m2-primary">CR Lớn</span>
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase bg-m2-surface-high text-m2-primary">
                      Phức tạp
                    </span>
                  </div>
                  <p className="text-xs text-m2-on-surface-variant leading-relaxed">
                    Tính năng mới, tái cấu trúc, mở rộng schema.
                  </p>
                  <div className="pt-2 mt-auto flex flex-wrap items-center gap-1 text-[11px] font-semibold border-t border-m2-outline-variant text-m2-primary">
                    <span>PRD</span>
                    <span className="text-m2-secondary">→</span>
                    <span>Coding</span>
                    <span className="text-m2-secondary">→</span>
                    <span>Test</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 pb-6">
              <Callout
                icon="🤖"
                text={
                  <>
                    <strong>AI tự nhận diện loại CR</strong> và hướng dẫn chọn hướng xử lý phù hợp
                    theo ma trận tác động, tối ưu tài nguyên và độ tin cậy code.
                  </>
                }
              />
            </div>
          </div>
        </div>

        {/* ── Value pillar ── */}
        <div className="mt-8 p-6 rounded-2xl flex items-start gap-4 bg-m2-surface border border-m2-outline-variant shadow-sm max-w-3xl mx-auto">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shrink-0 bg-m2-surface-high">
            🖥️
          </div>
          <div>
            <h4 className="font-bold text-base text-m2-primary">Thực chiến với Live Code</h4>
            <p className="text-sm text-m2-on-surface-variant mt-1.5 leading-relaxed">
              Quan sát trực tiếp cách áp dụng quy trình AI4SDLC vào case study thực tế — chỉ để
              hiểu cách làm, không nhận tài liệu.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          NOTICE BANNER
      ═══════════════════════════════════════════════════════ */}
      <section className="border-y border-m2-outline-variant bg-m2-surface-low">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-center sm:text-left">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-xl shrink-0 bg-m2-surface-high">
              🎬
            </div>
            <div>
              <p className="font-bold text-base text-m2-primary">
                Workshop đã kết thúc — xem lại trên YouTube
              </p>
              <p className="text-sm text-m2-on-surface-variant mt-1">
                Video buổi workshop sẽ được đăng tải sớm trên kênh YouTube của chúng tôi.
              </p>
            </div>
          </div>
          <YouTubeButton youtubeUrl={youtubeUrl} />
        </div>
      </section>
    </div>
  )
}

export default WorkshopPage
